import { z } from 'zod';

/** Taille maximale d'un instantané déposé (SPEC 12.1) : 10 Mo. */
export const TAILLE_MAX_IMPORT_OCTETS = 10 * 1024 * 1024;

/** Part de clients qui disparaissent au-delà de laquelle la confirmation est renforcée (SPEC 4.10). */
export const SEUIL_DISPARITION = 0.2;

/** Âge du dernier instantané au-delà duquel un rappel est affiché (SPEC 4.16). */
export const DELAI_RAPPEL_JOURS = 7;

/** Hôte SharePoint attendu (SPEC 12.1). */
export const HOTE_SHAREPOINT_ATTENDU = 'cedecgbo.sharepoint.com';

export const compteursSchema = z.object({
  /** Lignes présentes dans le fichier. */
  dansFichier: z.number().int(),
  ajoutes: z.number().int(),
  modifies: z.number().int(),
  /** Lignes en base qui ne sont plus dans le fichier (marquées absentes, réversible). */
  retires: z.number().int(),
  /** Lignes marquées absentes qui sont de retour dans le fichier. */
  reapparus: z.number().int(),
  inchanges: z.number().int(),
  /** Pour les lignes modifiées : nombre de lignes par colonne qui a changé (absent sur les anciens imports). */
  parChamp: z.record(z.string(), z.number().int()).optional(),
});
export type CompteursDto = z.infer<typeof compteursSchema>;

/** Champ écarté à la lecture : le nom seulement, jamais la valeur. */
export const champIgnoreSchema = z.object({
  liste: z.string(),
  nom: z.string(),
  occurrences: z.number().int(),
  /** Le nom évoque un contact (dirigeant, courriel, téléphone, commentaire). */
  sensible: z.boolean(),
});
export type ChampIgnoreDto = z.infer<typeof champIgnoreSchema>;

export const disparitionSchema = z.object({
  /** Clients actifs en base qui ne sont plus dans le fichier. */
  retires: z.number().int(),
  /** Clients actifs en base avant l'import. */
  actifsAvant: z.number().int(),
  /** Rapport retires / actifsAvant, entre 0 et 1. */
  part: z.number(),
  seuil: z.number(),
  /** Vrai si la part dépasse le seuil : la confirmation doit être renforcée. */
  confirmationRenforcee: z.boolean(),
});
export type DisparitionDto = z.infer<typeof disparitionSchema>;

/** Bilan commun à l'aperçu et à l'import appliqué. */
export const bilanSchema = z.object({
  clients: compteursSchema,
  accompagnements: compteursSchema,
  evenements: compteursSchema,
  adresses: z.object({
    /** Clients dont l'adresse (ou la raison sociale) a changé. */
    modifiees: z.number().int(),
    /** Parmi eux, ceux dont la position était verrouillée : ils repasseront en revue. */
    verrouillees: z.number().int(),
  }),
  /** Clients qui recevront une tâche de géocodage (nouveaux, adresse changée, sans position). */
  aGeocoder: z.number().int(),
  champsIgnores: z.array(champIgnoreSchema),
  disparition: disparitionSchema,
});
export type BilanImportDto = z.infer<typeof bilanSchema>;

export const apercuSchema = z.object({
  jeton: z.string(),
  expireLe: z.string(),
  fichier: z.object({
    nom: z.string(),
    octets: z.number().int(),
    sha256: z.string(),
  }),
  /** Version du format du fichier : 1 (stats/app, converti) ou 2. */
  versionSource: z.union([z.literal(1), z.literal(2)]),
  site: z.object({
    hote: z.string(),
    attendu: z.string(),
    ok: z.boolean(),
  }),
  extraitLe: z.string(),
  premierImport: z.boolean(),
  avertissements: z.array(z.string()),
  bilan: bilanSchema,
});
export type ApercuImportDto = z.infer<typeof apercuSchema>;

export const confirmerCorpsSchema = z.object({
  /** Obligatoire à vrai quand `disparition.confirmationRenforcee` est vrai. */
  confirmationRenforcee: z.boolean().default(false),
});
export type ConfirmerCorps = z.infer<typeof confirmerCorpsSchema>;

export const jetonParamsSchema = z.object({ jeton: z.string().min(16).max(128) });

export const confirmationSchema = z.object({
  importId: z.number().int(),
  importeLe: z.string(),
  bilan: bilanSchema,
  geocodage: z.object({
    planifiees: z.number().int(),
    /** Tâches non envoyées : les positions seront calculées au prochain import ou à la relance. */
    echecs: z.number().int(),
    /** Vrai si la file de tâches n'est pas démarrée sur ce processus. */
    fileIndisponible: z.boolean(),
  }),
});
export type ConfirmationImportDto = z.infer<typeof confirmationSchema>;

export const ligneHistoriqueSchema = z.object({
  id: z.number().int(),
  importeLe: z.string(),
  extraitLe: z.string(),
  site: z.string(),
  /** Nom lisible de la personne qui a importé (à défaut, l'identifiant enregistré). */
  par: z.string(),
  clients: z.object({
    dansFichier: z.number().int(),
    ajoutes: z.number().int(),
    modifies: z.number().int(),
    retires: z.number().int(),
  }),
  champsIgnores: z.number().int(),
});
export type LigneHistoriqueDto = z.infer<typeof ligneHistoriqueSchema>;

export const historiqueSchema = z.object({
  imports: z.array(ligneHistoriqueSchema),
  /** Âge en jours du dernier import, null s'il n'y en a pas. */
  ageJours: z.number().int().nullable(),
  /** Vrai s'il n'y a aucun import ou si le dernier a plus de `DELAI_RAPPEL_JOURS` jours. */
  rappel: z.boolean(),
});
export type HistoriqueImportDto = z.infer<typeof historiqueSchema>;

/** Ligne fautive signalée par l'aperçu (statut inconnu, référence manquante...). */
export const problemeSchema = z.object({
  liste: z.string(),
  id: z.number().int(),
  message: z.string(),
});
export type ProblemeImportDto = z.infer<typeof problemeSchema>;

/** Codes d'erreur de l'import, dans le champ `erreur` de la forme commune des erreurs. */
export const ERREURS_IMPORT = [
  'fichier_manquant',
  'fichier_trop_gros',
  'fichier_illisible',
  'site_inattendu',
  'donnees_invalides',
  'deja_importe',
  'jeton_inconnu',
  'confirmation_renforcee_requise',
  'import_en_cours',
] as const;
export type ErreurImport = (typeof ERREURS_IMPORT)[number];

export const erreurImportSchema = z.object({
  erreur: z.string(),
  message: z.string().optional(),
  details: z.unknown().optional(),
});
